import React, { useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'

import {
  selectAllEditFunnelStages,
  aggregateStagesBySectionId,
  setEditFunnelStagesOrder,
} from '@user/store/salesFunnel/editSlice'
import {
  DropResult,
  useDragData,
  DraggableItemWrapper,
} from '@user/utils/Draggable'
import Box from '@mui/material/Box'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { toast } from '@sales-center/components'
import { SalesFunnelStage } from './SalesFunnelStage'

type Props = {
  sectionId: string
  sectionName: string
}

const useStagesBySectionId = (sectionId: string) => {
  const stages = useSelector(selectAllEditFunnelStages)
  const filteredStages = useMemo(
    () => aggregateStagesBySectionId(stages, sectionId),
    [stages, sectionId],
  )
  return filteredStages
}

export function SalesFunnelStageList(props: Props) {
  const { sectionId, sectionName } = props

  const dispatch = useAppDispatch()

  const stages = useStagesBySectionId(sectionId)

  const onDragEnd = useCallback(
    (result: DropResult) => {
      const dropped = stages[result.sourceIndex]
      const droppedOn = stages[result.destinationIndex]

      if (droppedOn.locked || dropped.locked) {
        toast.warning('Cannot reorder System-Defined stages')
      } else {
        const newStages = [...stages]

        newStages.splice(result.sourceIndex, 1)
        newStages.splice(result.destinationIndex, 0, dropped)

        const update = newStages.map((s, i) => ({ id: s.id, order: i }))

        dispatch(setEditFunnelStagesOrder(update))
      }
    },
    [dispatch, stages],
  )

  const { containerRef, draggingIndex, itemRefs, offsets } = useDragData({
    dataList: stages,
    onDragEnd,
  })

  return (
    <Box mt={1.2} ref={containerRef} id="salesFunnelStages">
      {stages.map((row, i) => (
        <DraggableItemWrapper
          key={row.id}
          ref={(el) => (itemRefs.current[i] = el as HTMLDivElement | null)}
          draggingIndex={draggingIndex}
          index={i}
          offsets={offsets}
          itemHeight="fit"
        >
          <SalesFunnelStage
            stage={row}
            canHaveReasons={i == stages.length - 1}
            sectionName={sectionName}
          />
        </DraggableItemWrapper>
      ))}
    </Box>
  )
}
